/** Extract YouTube video ID from common URL formats. */
export const getYoutubeVideoId = (url) => {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  const shortMatch = trimmed.match(/youtu\.be\/([^?&#/]+)/i);
  if (shortMatch) return shortMatch[1];

  const embedMatch = trimmed.match(/youtube\.com\/embed\/([^?&#/]+)/i);
  if (embedMatch) return embedMatch[1];

  const shortsMatch = trimmed.match(/youtube\.com\/shorts\/([^?&#/]+)/i);
  if (shortsMatch) return shortsMatch[1];

  const liveMatch = trimmed.match(/youtube\.com\/live\/([^?&#/]+)/i);
  if (liveMatch) return liveMatch[1];

  const watchMatch = trimmed.match(
    /(?:youtube\.com\/watch\?(?:.*&)?v=|youtube\.com\/v\/)([^&#?/]+)/i
  );
  if (watchMatch) return watchMatch[1];

  return null;
};

const getVimeoVideoId = (url) => {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  const match = trimmed.match(/vimeo\.com\/(?:channels\/[^/]+\/|groups\/[^/]+\/videos\/|video\/)?(\d+)/i);
  return match ? match[1] : null;
};

const isDirectImageUrl = (url) =>
  /\.(jpe?g|png|gif|webp|avif)(\?.*)?$/i.test(String(url || "").trim());

/** Thumbnail URL for YouTube, Vimeo, or direct image links. */
export const getMediaThumbnailUrl = (url) => {
  if (!url || typeof url !== "string") return null;
  let trimmed = url.trim();
  if (!trimmed) return null;
  if (!/^https?:\/\//i.test(trimmed)) {
    trimmed = `https://${trimmed}`;
  }

  const youtubeId = getYoutubeVideoId(trimmed);
  if (youtubeId) {
    return `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
  }

  const vimeoId = getVimeoVideoId(trimmed);
  if (vimeoId) {
    return `https://vumbnail.com/${vimeoId}.jpg`;
  }

  if (isDirectImageUrl(trimmed)) {
    return trimmed;
  }

  return null;
};

export const getYoutubeThumbnailUrl = (url, quality = "hqdefault") => {
  const id = getYoutubeVideoId(url);
  return id ? `https://img.youtube.com/vi/${id}/${quality}.jpg` : null;
};

export const getYoutubeEmbedUrl = (url) => {
  const id = getYoutubeVideoId(url);
  return id ? `https://www.youtube.com/embed/${id}` : null;
};

export const getYoutubeWatchUrl = (url) => {
  const id = getYoutubeVideoId(url);
  return id ? `https://www.youtube.com/watch?v=${id}` : null;
};

export const getVimeoEmbedUrl = (url) => {
  const id = getVimeoVideoId(url);
  return id ? `https://player.vimeo.com/video/${id}` : null;
};

const DIRECT_VIDEO_EXT = /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i;

/** Resolve doctor introduction / video bio URL for embed or native playback. */
export const resolveIntroductionVideo = (rawUrl, uploadsBase = "") => {
  if (!rawUrl || typeof rawUrl !== "string") return null;
  let url = rawUrl.trim();
  if (!url) return null;

  if (!/^https?:\/\//i.test(url) && /^(www\.)?(youtube|youtu\.be|vimeo)/i.test(url)) {
    url = `https://${url}`;
  }

  const youtubeEmbed = getYoutubeEmbedUrl(url);
  if (youtubeEmbed) {
    return {
      kind: "embed",
      embedUrl: youtubeEmbed,
      watchUrl: getYoutubeWatchUrl(url),
      thumbnailUrl: getYoutubeThumbnailUrl(url),
    };
  }

  const vimeoEmbed = getVimeoEmbedUrl(url);
  if (vimeoEmbed) {
    return {
      kind: "embed",
      embedUrl: vimeoEmbed,
      watchUrl: /^https?:\/\//i.test(url) ? url : `https://${url}`,
      thumbnailUrl: getMediaThumbnailUrl(url),
    };
  }

  if (/^https?:\/\//i.test(url)) {
    if (DIRECT_VIDEO_EXT.test(url) || url.includes("w3schools.com") || url.includes(".mp4")) {
      return { kind: "file", src: url };
    }
    return null;
  }

  const path = url.replace(/^\/+/, "");
  const src = path.startsWith("uploads/")
    ? `${uploadsBase}/${path}`
    : `${uploadsBase}/uploads/${path}`;
  return { kind: "file", src };
};
